import startCase from "lodash/startCase";
import React, { useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DialogProps } from "@material-ui/core/Dialog";

import { EMPTY_STRING } from "common/constants";
import ActionPopup from "components/ActionPopup";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useInfiniteScrollToggle } from "hooks/useInfiniteScrollToggle";
import { useDebouncedNameValue } from "hooks/useSearch";
import { getStringMatchQueryField } from "utils/required-values-utils";
import HorseDetails from "./HorseDetails";
import HorseSearch from "./HorseSearch";
import { getHorseShortInfoFromHorse } from "./helper";

import useStyles from "./styles";

interface IProps extends DialogProps {
  onClose: () => void;
  onAddHorseToTrack?: (horsePatch?: racehorse360.IHorse) => void;
  selectedHorse?: racehorse360.IHorse;
}

const MIN_CHARS_NUMBER_FOR_SEARCH = 3;

const AddHorseToTrackPopup = (props: IProps) => {
  const { onClose, onAddHorseToTrack, selectedHorse } = props;

  const classes = useStyles();
  const { facilities } = useAppSelector(state => state.facilities);
  useInfiniteScrollToggle();

  const [currentHorse, setCurrentHorse] =
    useState<racehorse360.IHorseShortInfo>(
      getHorseShortInfoFromHorse(selectedHorse)
    );
  const [selectedFacility, setSelectedFacility] =
    useState<racehorse360.IFacility>(selectedHorse?.currentFacility);
  const [searchQuery, setSearchQuery] = useState(EMPTY_STRING);

  const debouncedSearchQuery = useDebouncedNameValue(searchQuery, {
    timeout: 500,
    startFrom: MIN_CHARS_NUMBER_FOR_SEARCH
  });
  const stringMatchQuerySimilarOrContains = getStringMatchQueryField(
    "similarOrContains",
    debouncedSearchQuery
  );

  const { usePullHorsesShortInfo, useAssignHorseToFacility } =
    useRacehorse360Api();

  const { mutateAsync: assignHorseToFacility } = useAssignHorseToFacility();

  const {
    data,
    isLoading: isHorsesShortInfoLoading,
    isFetching: isHorsesShortInfoFetching
  } = usePullHorsesShortInfo(
    {
      query: {
        filterString: stringMatchQuerySimilarOrContains,
        filterBy: ["horseName", "horseChipNumber"],
        exclude_12yoHorses: true
      },
      getOptions: {
        select: [
          "id",
          "horseName",
          "horseRegistrationNumber",
          "trainerName",
          "currentFacilityId"
        ]
      }
    },
    {
      enabled: Boolean(stringMatchQuerySimilarOrContains)
    }
  );

  const horsesInfo = data?.horsesShortInfo || [];
  const styledFacilityName = startCase(selectedFacility?.name.toLowerCase());

  const isSubmitButtonDisabled = [
    !selectedFacility,
    currentHorse?.currentFacilityId === selectedFacility?.id
  ].some(Boolean);

  const isLoading = [isHorsesShortInfoLoading, isHorsesShortInfoFetching].some(
    Boolean
  );

  const handleSetSelectedFacility = (facilityId: string) => {
    const currentSelectedFacility = facilities.find(
      facility => facility.id === facilityId
    );

    setSelectedFacility(currentSelectedFacility);
  };

  const handleHorseUnselect = () => {
    setCurrentHorse(null);
    setSelectedFacility(null);
  };

  const handleHorseSelect = (item: racehorse360.IHorseShortInfo) => {
    setCurrentHorse(item);
    handleSetSelectedFacility(item.currentFacilityId);
  };

  const handleSubmit = () => {
    if (selectedFacility.id === currentHorse.currentFacilityId) {
      onClose();
      return;
    }

    return assignHorseToFacility({
      horseId: currentHorse.id,
      facilityId: selectedFacility.id,
      getOptions: {
        select: [
          "currentFacility.name",
          "currentFacility.workout_required_days_off"
        ]
      }
    }).then(horse => {
      onAddHorseToTrack
        ? onAddHorseToTrack({ currentFacility: horse.currentFacility })
        : onClose();
    });
  };

  const hasCurrentHorse = Boolean(currentHorse);

  const successNotification = `${currentHorse?.horseName} has successfully been added to ${styledFacilityName}`;

  const backOrCloseButtonText = hasCurrentHorse ? "Back" : "Close";
  const backOrCloseButtonHandler = hasCurrentHorse
    ? handleHorseUnselect
    : onClose;

  return (
    <ActionPopup
      className={currentHorse && classes.addHorseToTrackPopup}
      open={true}
      onClose={null}
      title={"Choose a Horse to Add to a Track"}
      cancelButtonText={backOrCloseButtonText}
      submitButtonText={"Submit Horse to Track"}
      onCancel={backOrCloseButtonHandler}
      onSubmit={handleSubmit}
      disabledAction={isSubmitButtonDisabled}
      scrollable={Boolean(currentHorse)}
      notification={successNotification}
    >
      {!hasCurrentHorse && (
        <HorseSearch
          horsesInfo={horsesInfo}
          isDataLoading={isLoading}
          searchQuery={searchQuery}
          onChangeSearchQuery={setSearchQuery}
          onHorseSelect={handleHorseSelect}
        />
      )}

      {hasCurrentHorse && (
        <HorseDetails
          horseData={currentHorse}
          onHorseUnselect={handleHorseUnselect}
          selectedFacilityId={selectedFacility?.id}
          onSetSelectedFacility={handleSetSelectedFacility}
        />
      )}
    </ActionPopup>
  );
};

export default React.memo(AddHorseToTrackPopup);
